
import { Component, Vue, Watch } from "vue-property-decorator";
import ActivityCard from "./activityCard.vue";
import { CrudService } from "../../../core/services/crudService";
import { Settings } from "../../../core/services/configService";
import store from "../../../core/store";
import { DateHelper } from "../../../core/helpers/dateHelper";
import { PublicActivityStatusFilters, PublicActivityTypeFilters } from "../../constants";

interface OptionItem {
  label: string;
  value: string;
}

interface ActivityResultItem {
  activity: any;
  [key: string]: any;
}

@Component({
  components: {
    ActivityCard,
  },
})
export default class ActivitiesPage extends Vue {
  private readonly pageSize: number = 12;
  private readonly paginationWindow: number = 5;
  private readonly defaultType: string = PublicActivityTypeFilters.Registration;
  private readonly defaultStatus: string = PublicActivityStatusFilters.Ongoing;
  private readonly allowedTypeValues: string[] = Object.values(PublicActivityTypeFilters) as string[];
  private readonly allowedStatusValues: string[] = Object.values(PublicActivityStatusFilters) as string[];
  private readonly crudService = new CrudService();
  private suppressRouteWatcher: boolean = false;

  items: ActivityResultItem[] = [];
  totalCount: number = 0;
  firstRecord: number = 0;
  lastRecord: number = this.pageSize;
  loading: boolean = false;
  statusOptions: OptionItem[] = [];
  typeOptions: OptionItem[] = [];
  selectedStatus: string = this.defaultStatus;
  selectedType: string = this.defaultType;

  async mounted() {
    this.configureOptions();
    this.syncStateFromRoute();
    this.resetPagination();
    await this.loadActivities();
  }

  @Watch("$route.query", { deep: true })
  async onRouteQueryChanged() {
    if (this.suppressRouteWatcher) {
      return;
    }
    const changed = this.syncStateFromRoute();
    if (changed) {
      this.resetPagination();
      await this.loadActivities();
    }
  }

  get pageTitle() {
    return this.selectedType === PublicActivityTypeFilters.Registration ? this.$t("Registrations") : this.$t("Activities");
  }

  get emptyStateText() {
    return this.selectedType === PublicActivityTypeFilters.Registration
      ? this.$t("No registrations match your filters.")
      : this.$t("No activities match your filters.");
  }

  get currentPage() {
    return Math.floor(this.firstRecord / this.pageSize) + 1;
  }

  get pageCount() {
    return this.totalCount > 0 ? Math.ceil(this.totalCount / this.pageSize) : 1;
  }

  get hasPrevious() {
    return this.firstRecord > 0;
  }

  get hasNext() {
    return this.firstRecord + this.pageSize < this.totalCount;
  }

  get paginationPages() {
    const total = this.pageCount;
    const pages: number[] = [];
    const windowSize = Math.min(this.paginationWindow, total);
    let start = Math.max(1, this.currentPage - Math.floor(windowSize / 2));
    if (start + windowSize - 1 > total) {
      start = Math.max(1, total - windowSize + 1);
    }
    for (let i = 0; i < windowSize; i++) {
      pages.push(start + i);
    }
    return pages;
  }

  get settings() {
    return store.state.settings;
  }

  get hasItems() {
    return this.items.length > 0;
  }

  configureOptions() {
    this.statusOptions = [
      this.buildOption("Ongoing", PublicActivityStatusFilters.Ongoing),
      this.buildOption("Upcoming", PublicActivityStatusFilters.Upcoming),
      this.buildOption("Finished", PublicActivityStatusFilters.Finished),
    ];
    this.typeOptions = [
      this.buildOption("Registrations", PublicActivityTypeFilters.Registration),
      this.buildOption("Activities", PublicActivityTypeFilters.Activity),
    ];
  }

  async changeStatus(value: string) {
    await this.applyFilterChange(() => this.applyStatus(value));
  }

  async changeType(value: string) {
    await this.applyFilterChange(() => this.applyType(value));
  }

  async previousPage() {
    if (!this.hasPrevious) {
      return;
    }
    await this.goTo(this.firstRecord - this.pageSize);
  }

  async nextPage() {
    if (!this.hasNext) {
      return;
    }
    await this.goTo(this.firstRecord + this.pageSize);
  }

  async navigateTo(page: number) {
    if (page === this.currentPage) {
      return;
    }
    const targetFirst = (page - 1) * this.pageSize;
    await this.goTo(targetFirst);
  }

  getActivityUrl(id: number) {
    return `/activity/${id}`;
  }

  formatDate(date: string) {
    const parsed = DateHelper.toDate(date, DateHelper.systemFormat);
    return DateHelper.formatDate(parsed, "yyyy-MM-dd");
  }

  getImageProduct(name: string) {
    if (name) {
      return `${this.settings.Uploads}/${name}`;
    }
    return "/img/activity-default.png";
  }

  private async loadActivities() {
    this.loading = true;
    try {
      const data = await this.crudService.httpGet(this.buildRequestUrl());
      if (!data || !data.ok) {
        console.error("Failed loading activities page");
        this.clearResults();
        return;
      }
      this.applyResponseModel(data.model);
    } catch (error) {
      console.error(error);
      this.clearResults();
    } finally {
      this.loading = false;
    }
  }

  private applyType(value?: string | string[]): boolean {
    const normalized = this.normalizeType(value);
    if (normalized === this.selectedType) {
      return false;
    }
    this.selectedType = normalized;
    return true;
  }

  private applyStatus(value?: string | string[]): boolean {
    const normalized = this.normalizeStatus(value);
    if (normalized === this.selectedStatus) {
      return false;
    }
    this.selectedStatus = normalized;
    return true;
  }

  private normalizeType(value?: string | string[]): string {
    const raw = Array.isArray(value) ? value[0] : value;
    if (!raw) {
      return this.defaultType;
    }
    const normalized = raw.toLowerCase();
    return this.allowedTypeValues.includes(normalized) ? normalized : this.defaultType;
  }

  private normalizeStatus(value?: string | string[]): string {
    const raw = Array.isArray(value) ? value[0] : value;
    if (!raw) {
      return this.defaultStatus;
    }
    const normalized = raw.toLowerCase();
    return this.allowedStatusValues.includes(normalized) ? normalized : this.defaultStatus;
  }

  private syncStateFromRoute() {
    const typeChanged = this.applyType(this.$route.query.type);
    const statusChanged = this.applyStatus(this.$route.query.status);
    return typeChanged || statusChanged;
  }

  private resetPagination() {
    this.setPaginationBounds(0);
  }

  private async updateRoute() {
    this.suppressRouteWatcher = true;
    try {
      await this.$router
        .replace({
          path: this.$route.path,
          query: {
            ...this.$route.query,
            type: this.selectedType,
            status: this.selectedStatus,
          },
        })
        .catch(() => undefined);
    } finally {
      this.$nextTick(() => {
        this.suppressRouteWatcher = false;
      });
    }
  }

  private buildOption(labelKey: string, value: string): OptionItem {
    return { label: this.$t(labelKey).toString(), value };
  }

  private async applyFilterChange(changeFn: () => boolean) {
    if (!changeFn()) {
      return;
    }
    this.resetPagination();
    await this.loadActivities();
    await this.updateRoute();
  }

  private async goTo(first: number) {
    const target = Math.max(0, first);
    if (target === this.firstRecord) {
      return;
    }
    this.setPaginationBounds(target);
    await this.loadActivities();
  }

  private setPaginationBounds(first: number) {
    this.firstRecord = Math.max(0, first);
    this.lastRecord = this.firstRecord + this.pageSize;
  }

  private buildRequestUrl() {
    const params = new URLSearchParams({
      startRow: String(this.firstRecord),
      endRow: String(this.lastRecord),
      status: this.selectedStatus,
      type: this.selectedType,
    });
    return `${Settings.HostName}/api/publicactivities/list?${params.toString()}`;
  }

  private applyResponseModel(model: any) {
    const safeModel = model ?? {};
    const entries = Array.isArray(safeModel.activities) ? safeModel.activities : [];
    this.items = entries.filter((entry: any) => entry && entry.activity);
    this.totalCount = typeof safeModel.totalCount === "number" ? safeModel.totalCount : 0;
    if (typeof safeModel.startRow === "number") {
      this.firstRecord = safeModel.startRow;
    }
    if (typeof safeModel.endRow === "number") {
      this.lastRecord = safeModel.endRow;
    }
  }

  private clearResults() {
    this.items = [];
    this.totalCount = 0;
  }
}
